import React, { useCallback, useContext, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';

import { PRELOAD_SSR_DATA, CLEAR_SSR_DATA } from '../../base/actions/ssr';

const SSRDataContext = React.createContext();

const defaultState = {};

const propTypes = {

	/**
	 * The initial preloaded data: on the server the bag collected while
	 * running the matched routes' `needs()` statics; on the client the
	 * server-serialized `window.__PRELOADED_STATE__`.
	 */
	initialData: PropTypes.shape({}),

	/**
	 * The component children.
	 */
	children: PropTypes.node.isRequired,

};

/**
 * Reducer for the preloaded data required for SSR: preloads merge into the
 * bag, clears empty it (the same semantics the old redux ssr reducer had).
 *
 * @param   {Object} state   The current state.
 * @param   {Object} action  The action being performed.
 *
 * @returns {Object}         The new state.
 */
function ssrDataReducer(state, action) {

	switch (action.type) {

	case PRELOAD_SSR_DATA:

		return Object.assign({}, state, action.data);

	case CLEAR_SSR_DATA:

		return Object.assign({}, defaultState);

	default:

		return state;

	}

}

/**
 * Holds the SSR-preloaded data bag (replaces the redux store, whose entire
 * state was this one object). Containers read it in place of the old
 * `state.data.base.ssr` and merge their client-side fetches into it so
 * hydration and subsequent navigation share one cache.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object} The provider component.
 */
function SSRDataProvider(props) {

	const { initialData, children } = props;

	const [ssrData, dispatch] = useReducer(ssrDataReducer, initialData || defaultState);

	const preloadDataForSSR = useCallback((data) => {
		dispatch({ type: PRELOAD_SSR_DATA, data });
	}, []);

	const clearDataForSSR = useCallback(() => {
		dispatch({ type: CLEAR_SSR_DATA });
	}, []);

	const value = useMemo(() => ({
		ssrData,
		preloadDataForSSR,
		clearDataForSSR,
	}), [ssrData, preloadDataForSSR, clearDataForSSR]);

	return (
		<SSRDataContext.Provider value={value}>
			{children}
		</SSRDataContext.Provider>
	);

}

SSRDataProvider.propTypes = propTypes;

/**
 * The SSR-preloaded data hook.
 *
 * @returns {Object} { ssrData, preloadDataForSSR, clearDataForSSR }
 */
function useSSRData() {

	const context = useContext(SSRDataContext);

	if (!context) {
		throw new Error('useSSRData must be used within a SSRDataProvider');
	}

	return context;

}

export {
	SSRDataProvider,
	useSSRData,
};
